import { useCallback } from 'react'
import { useCurrency } from 'contexts'

export const useCurrencyConversion = () => {
  const { currentCurrency, convertCurrency, currencies } = useCurrency()

  const formatAmount = useCallback(
    (amount: number, currencyCode: string = currentCurrency, locale: string = 'en-US'): string => {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount)
      } catch (error) {
        return `${currencyCode} ${amount.toFixed(2)}`
      }
    },
    [currentCurrency],
  )

  const convertValueByYear = useCallback(
    async (
      value: number,
      year: number,
      fromCurrency: string = 'USD',
      toCurrency: string = currentCurrency,
    ): Promise<number | null> => {
      if (fromCurrency === toCurrency) return value

      return await convertCurrency(value, fromCurrency, toCurrency, year)
    },
    [currentCurrency, convertCurrency],
  )

  const getCurrentCurrencySymbol = useCallback((): string => {
    const currentCurrencyData = currencies.find(c => c.code === currentCurrency)
    return currentCurrencyData?.symbol || currentCurrency
  }, [currencies, currentCurrency])

  const convertRevenueByYear = useCallback(
    async (
      revenueByYear: Record<string, number>,
      fromCurrency: string = 'USD',
      toCurrency: string = currentCurrency,
    ): Promise<Record<string, number> | null> => {
      if (fromCurrency === toCurrency) return revenueByYear

      const convertedRevenue: Record<string, number> = {}

      for (const [year, amount] of Object.entries(revenueByYear)) {
        const convertedAmount = await convertCurrency(amount, fromCurrency, toCurrency, parseInt(year))
        if (convertedAmount !== null) {
          convertedRevenue[year] = convertedAmount
        }
      }

      return Object.keys(convertedRevenue).length > 0 ? convertedRevenue : null
    },
    [currentCurrency, convertCurrency],
  )

  const calculateTotalRevenue = useCallback(
    async (
      revenueSources: Array<{ revenueByYear?: { total?: Record<string, number> } }>,
      fromCurrency: string = 'USD',
      toCurrency: string = currentCurrency,
    ): Promise<number | null> => {
      if (fromCurrency === toCurrency) {
        let total = 0
        for (const source of revenueSources) {
          if (source.revenueByYear?.total) {
            for (const amount of Object.values(source.revenueByYear.total)) {
              total += amount
            }
          }
        }
        return total
      }

      let totalConverted = 0

      for (const source of revenueSources) {
        if (source.revenueByYear?.total) {
          for (const [year, amount] of Object.entries(source.revenueByYear.total)) {
            const convertedAmount = await convertCurrency(amount, fromCurrency, toCurrency, parseInt(year))
            if (convertedAmount !== null) {
              totalConverted += convertedAmount
            }
          }
        }
      }

      return totalConverted > 0 ? totalConverted : null
    },
    [currentCurrency, convertCurrency],
  )

  return {
    formatAmount,
    currentCurrency,
    convertValueByYear,
    getCurrentCurrencySymbol,
    convertRevenueByYear,
    calculateTotalRevenue,
  }
}
